import { useCallback, useEffect, useMemo, useState } from "react";

export type MessagesPanelState = 'minimized' | 'standard' | 'maximized';
export type MaximizedPanel = 'messages' | 'notes' | null;

interface UseMessagesPanelProps {
  hasNewMessage: boolean;
  featureEnabled: boolean;
}

export interface UseMessagesPanelReturn {
  panelState: MessagesPanelState;
  maximizedPanel: MaximizedPanel;
  minimize: () => void;
  restore: () => void;
  maximize: (panel: 'messages' | 'notes') => void;
  closeMaximized: () => void;
  isMinimized: boolean;
  isMaximized: boolean;
  isStandard: boolean;
}

const NOOP = () => { /* no-op for disabled state */ };

export const useMessagesPanel = ({ hasNewMessage, featureEnabled }: UseMessagesPanelProps): UseMessagesPanelReturn => {
  const [panelState, setPanelState] = useState<MessagesPanelState>('standard');
  const [maximizedPanel, setMaximizedPanel] = useState<MaximizedPanel>(null);

  useEffect(() => {
    if (!featureEnabled) return;
    if (hasNewMessage) {
      setPanelState('standard');
    } else {
      setPanelState((prev) => prev === 'standard' ? 'minimized' : prev);
    }
  }, [hasNewMessage, featureEnabled]);

  const minimize = useCallback(() => {
    setPanelState('minimized');
    setMaximizedPanel(null);
  }, []);

  const restore = useCallback(() => {
    setPanelState('standard');
    setMaximizedPanel(null);
  }, []);

  const maximize = useCallback((panel: 'messages' | 'notes') => {
    setPanelState('maximized');
    setMaximizedPanel(panel);
  }, []);

  const closeMaximized = useCallback(() => {
    setPanelState('standard');
    setMaximizedPanel(null);
  }, []);

  const derived = useMemo(() => ({
    isMinimized: panelState === 'minimized',
    isMaximized: panelState === 'maximized',
    isStandard: panelState === 'standard',
  }), [panelState]);

  if (!featureEnabled) {
    return {
      panelState: 'standard',
      maximizedPanel: null,
      minimize: NOOP,
      restore: NOOP,
      maximize: NOOP,
      closeMaximized: NOOP,
      isMinimized: false,
      isMaximized: false,
      isStandard: true,
    };
  }

  return {
    panelState,
    maximizedPanel,
    minimize,
    restore,
    maximize,
    closeMaximized,
    ...derived,
  };
};
